import { Link, useLocation } from 'react-router-dom';
import {
    useEffect,
    useId,
    useState,
    type ReactNode,
} from 'react';
import { canAccessPermission, canAccessRoute, useAdminAccess } from '@/shared/model/adminAccess';
import { RoutePath } from '@/core/config/routeConfig';
import type { ApiUserRole as ApiUserRoleType } from '@/core/generated/api';

import {
    LuPanelLeft,
    LuX,
    LuChevronDown,
} from 'react-icons/lu';
import { useChatDialogStore } from '@/entities/ChatDialog/model/store';
import { useUnreadMessages } from '@/entities/AppChat/model/useUnreadMessages';
import { useOrderProblemCount } from '@/core/hooks/useOrderProblemCount';
import { SidebarIcon } from './SidebarIcon';
import {
    mainNavItem,
    operatorTasksNavItem,
    navGroups,
    filterNavigationGroups,
    type NavItem,
    type NavGroup,
} from './navigation.config';
import styles from './Sidebar.module.scss';

interface SidebarProps {
    onClose?: () => void;
    onNavigate?: () => void;
    collapsed?: boolean;
    onToggleCollapse?: () => void;
    roleCode: ApiUserRoleType | null;
    taskOverdueCount?: number;
}

/** Отображает навигацию административной панели. */
const Sidebar = (
    {
        onClose,
        onNavigate,
        collapsed = false,
        onToggleCollapse,
        roleCode,
        taskOverdueCount = 0,
    }: SidebarProps
) => {
    const location = useLocation();
    const navigationId = useId();

    const [closedGroups, setClosedGroups] = useState<Record<string, boolean>>(() => {
        try {
            const saved: unknown = JSON.parse(localStorage.getItem('vox.sidebar.groups.v1') ?? '{}');

            if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return {};

            return Object.fromEntries(Object.entries(saved)
                .filter((
                    entry
                ): entry is [string, boolean] => typeof entry[1] === 'boolean'));
        } catch {
            return {};
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem('vox.sidebar.groups.v1', JSON.stringify(closedGroups));
        } catch {
            return;
        }
    }, [closedGroups]);

    const accessSnapshot = useAdminAccess((state) => state.snapshot);
    const nativeUnread = useUnreadMessages(canAccessPermission(accessSnapshot, 'admin.chats.view'));
    const externalUnread = useChatDialogStore((state) => state.counters?.unreadMessages ?? 0);

    const unreadCount = nativeUnread + (canAccessRoute(accessSnapshot, RoutePath.chatDialogs)
        ? externalUnread
        : 0);

    const problemCount = useOrderProblemCount(canAccessRoute(accessSnapshot, RoutePath.orderProblems));
    const environment = import.meta.env.VITE_APP_ENV || 'development';
    const isProd = environment === 'production';

    const envName = environment === 'development'
        ? 'DEV'
        : 'TEST';

    const environmentClass = isProd
        ? ''
        : environment === 'development'
            ? styles.development
            : styles.test;

    /** Проверяет доступность пункта навигации */
    const canViewItem = (item: NavItem) => (
        (!item.allowedRoles || (roleCode != null && item.allowedRoles.includes(roleCode)))
        && canAccessRoute(accessSnapshot, item.accessRoute ?? item.path)
    );

    const visibleNavGroups = filterNavigationGroups(navGroups, canViewItem);

    /** Показывает доступный раздел и его счётчик */
    const renderNavItem = (item: NavItem) => {
        const isActive = location.pathname === item.path
            || item.path !== RoutePath.main && location.pathname.startsWith(item.path + '/');

        const count = item.path === RoutePath.operatorTasks
            ? taskOverdueCount
            : item.path === RoutePath.chats
                ? unreadCount
                : item.path === RoutePath.orderProblems
                    ? problemCount
                    : 0;

        const countLabel = item.path === RoutePath.operatorTasks
            ? 'Просроченных задач'
            : item.path === RoutePath.chats
                ? 'Непрочитанных сообщений'
                : 'Активных проблем заказов';

        return <li key={item.path}>
            <Link
                to={item.path}
                onClick={onNavigate}
                className={`${styles.navLink} ${isActive
                    ? styles.active
                    : ''}`}
                aria-label={collapsed
                    ? item.title
                    : undefined
                }
                aria-current={isActive
                    ? 'page'
                    : undefined
                }
                title={collapsed
                    ? item.title
                    : undefined
                }
            >
                <span
                    className={styles.icon}
                    aria-hidden="true"
                >
                    <SidebarIcon name={item.icon} />
                </span>
                {!collapsed &&
                    <span>{item.title}</span>
                }
                {count > 0 &&
                    <span
                        className={styles.unreadCount}
                        aria-label={`${countLabel}: ${count}`}
                    >
                        {count > 99
                            ? '99+'
                            : count}
                    </span>
                }
            </Link>
        </li>;
    };

    /** Показывает раздел и сворачиваемые подгруппы */
    const renderNavGroup = (group: NavGroup, key: string, nested = false): ReactNode => {
        const open = collapsed || !closedGroups[key];
        const groupId = `${navigationId}-${encodeURIComponent(key)}`;

        return <li
            key={key}
            className={nested
                ? styles.subgroup
                : styles.group
            }
        >
            {!collapsed &&
                <button
                    type="button"
                    className={nested
                        ? styles.subgroupTitle
                        : styles.groupTitle
                    }
                    aria-expanded={open}
                    aria-controls={groupId}
                    onClick={() => setClosedGroups(current => ({
                        ...current,
                        [key]: !current[key]
                    }))}
                >
                    <span>{group.title}</span>
                    <LuChevronDown
                        aria-hidden="true"
                        className={open
                            ? undefined
                            : styles.chevronClosed
                        }
                    />
                </button>
            }
            {open &&
                <ul
                    id={groupId}
                    className={nested && !collapsed
                        ? styles.nestedList
                        : styles.list
                    }
                >
                    {group.items.map(renderNavItem)}
                    {group.subgroups?.map(subgroup => renderNavGroup(subgroup, `${key}/${subgroup.title}`, true))}
                </ul>
            }
        </li>;
    };

    return (
        <aside className={`${styles.sidebar} ${environmentClass} ${collapsed
            ? styles.collapsed
            : ''}`}>
            {onClose && (
                <div className={styles.mobileCloseRow}>
                    <button
                        type="button"
                        className={styles.mobileCloseButton}
                        aria-label="Закрыть меню"
                        onClick={onClose}
                    >
                        <LuX aria-hidden="true" />
                    </button>
                </div>
            )}

            {!isProd &&
                <div
                    className={styles.environmentWatermark}
                    aria-hidden="true"
                >
                    {envName}
                </div>
            }

            <div className={`${styles.header} ${onClose
                ? styles.mobileHeader
                : ''}`}>
                <div className={styles.brandRow}>
                    {!collapsed &&
                        <h2 className={styles.brand}>
                            VOX.Manage
                            {!isProd &&
                                <span className={styles.environmentBadge}>
                                    {envName}
                                </span>
                            }
                        </h2>
                    }
                    {onToggleCollapse &&
                        <button
                            type="button"
                            className={styles.collapseButton}
                            aria-label={collapsed
                                ? 'Развернуть меню'
                                : 'Свернуть меню'
                            }
                            aria-expanded={!collapsed}
                            onClick={onToggleCollapse}
                        >
                            <LuPanelLeft aria-hidden="true" />
                        </button>
                    }
                </div>
            </div>

            <nav
                className={styles.navigation}
                aria-label="Разделы админки"
            >
                <ul className={styles.list}>
                    {[mainNavItem, operatorTasksNavItem].filter(canViewItem).map(renderNavItem)}
                    {visibleNavGroups.map(group => renderNavGroup(group, group.title))}
                </ul>
            </nav>

        </aside>
    );
};

export default Sidebar;
